import { useEffect, useRef } from "react";

/**
 * Observes the ref element. When it enters the viewport, adds `is-visible`
 * to the element itself AND to all descendants with the `.reveal` class.
 * Disconnects after reveal.
 */
export function useScrollReveal<T extends HTMLElement = HTMLElement>(
  options?: IntersectionObserverInit,
) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const revealAll = () => {
      el.classList.add("is-visible");
      el.querySelectorAll(".reveal").forEach((c) => c.classList.add("is-visible"));
    };

    if (typeof IntersectionObserver === "undefined") {
      revealAll();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            revealAll();
            observer.disconnect();
          }
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -10% 0px", ...options },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}
